<template>
  <div class="timezone-selector tw-mb-3">
    <label v-if="label" class="tw-block tw-font-medium tw-mb-2">{{ label }}</label>
    
    <v-select
      :value="value"
      @input="$emit('input', $event)"
      class="athlisis-select"
      :options="groupedTimezones"
      :reduce="option => option.value"
      :placeholder="computedPlaceholder"
      :class="{ 'is-invalid': error }"
      :filterable="true"
      :searchable="true"
      :clearable="false"
      label="label"
    >
      <template #no-options>
        {{ $t('components.timezoneSelector.noOptions') }}
      </template>
    </v-select>
    <div v-if="error" class="tw-text-red-600 tw-text-sm tw-mt-1">{{ error }}</div>
    <small v-if="helpText" class="tw-block tw-text-gray-600 tw-text-sm tw-mt-1">{{ helpText }}</small>

        
    <!-- Current timezone detection alert -->
    <div 
      v-if="showCurrentTimezoneAlert" 
      class="tw-flex tw-items-center tw-justify-between tw-mt-3 tw-py-2 tw-px-3 tw-mb-2 tw-bg-amber-50 tw-border tw-border-amber-300 tw-rounded"
    >
      <div class="tw-flex-grow">
        <div class="tw-text-amber-900 tw-text-sm">
          <div class="tw-flex tw-items-start">
            <i class="icon-info tw-mr-2 tw-mt-0.5"></i>
            <div>
              <div class="tw-font-semibold tw-mb-1">{{ $t('components.timezoneSelector.mismatchWarning.title') }}</div>
              <div class="tw-text-xs tw-text-amber-800">
                {{ $t('components.timezoneSelector.mismatchWarning.detected') }}: <strong>{{ currentTimezoneDisplay }}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
      <b-button 
        variant="primary"
        size="sm"
        class="tw-ml-3 tw-flex-shrink-0"
        @click="setToCurrentTimezone"
      >
        <i class="icon-check tw-mr-1"></i>
        {{ $t('components.timezoneSelector.mismatchWarning.useDetected') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import { getBrowserTimezone } from '@utils/timezone'

export default {
  name: 'timezone-selector',
  components: {
    vSelect
  },
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    error: {
      type: String,
      default: ''
    },
    helpText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      groupedTimezones: [],
      currentTimezone: null
    }
  },
  computed: {
    computedPlaceholder() {
      return this.placeholder || this.$t('components.timezoneSelector.placeholder')
    },
    showCurrentTimezoneAlert() {
      return this.currentTimezone && this.currentTimezone !== this.value
    },
    currentTimezoneDisplay() {
      if (!this.currentTimezone) return ''
      const option = this.groupedTimezones.find(tz => tz.value === this.currentTimezone)
      return option ? option.label : this.currentTimezone
    }
  },
  created() {
    this.detectCurrentTimezone()
    this.loadTimezones()
  },
  methods: {
    detectCurrentTimezone() {
      this.currentTimezone = getBrowserTimezone()
    },
    
    setToCurrentTimezone() {
      if (this.currentTimezone) {
        this.$emit('input', this.currentTimezone)
      }
    },
    
    loadTimezones() {
      try {
        // Get all supported timezones from the browser
        const timezones = Intl.supportedValuesOf('timeZone')
        
        // Create flat array of timezone options
        const options = timezones.map(tz => {
          const displayName = tz.replace(/_/g, ' ')
          const localTime = this.getLocalTime(tz)
          const offset = this.getTimezoneOffset(tz)
          
          return {
            value: tz,
            label: `${displayName} (${offset}) - ${localTime}`,
            displayName: displayName
          }
        })
        
        // Sort alphabetically by timezone name
        this.groupedTimezones = options.sort((a, b) => {
          return a.displayName.localeCompare(b.displayName)
        })
      } catch (error) {
        console.error('Error loading timezones:', error)
        // Fallback to a basic list if the API is not supported
        this.groupedTimezones = [
          { value: 'UTC', label: 'UTC - 00:00 (UTC+0)', displayName: 'UTC' }
        ]
      }
    },
    
    getLocalTime(timezone) {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
        return formatter.format(new Date())
      } catch (error) {
        return '00:00'
      }
    },
    
    getTimezoneOffset(timezone) {
      try {
        const now = new Date()
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          timeZoneName: 'shortOffset'
        })
        
        const parts = formatter.formatToParts(now)
        const offsetPart = parts.find(part => part.type === 'timeZoneName')
        
        if (offsetPart && offsetPart.value) {
          return offsetPart.value.replace('GMT', 'UTC')
        }
        
        // Fallback: calculate offset manually
        const tzDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
        const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
        const offset = (tzDate - utcDate) / (1000 * 60 * 60)
        const sign = offset >= 0 ? '+' : ''
        return `UTC${sign}${offset}`
      } catch (error) {
        return 'UTC'
      }
    }
  }
}
</script>

